// Story events that can be randomly selected based on constraints

// FIXED EVENT - Always happens on day 5
const THE_DECISION = {
  day: 5,
  name: 'The Decision',
  icon: '🔱',
  type: 'story_choice',
  description: 'Five days into your settlement, you face a defining choice about its future...',
  choices: [
    {
      id: 'defense',
      label: 'Focus on Defense',
      description: 'Fortify our settlement against threats',
      effects: {
        wallProgress: 5,
        morale: 5,
        message: 'Your people feel safer behind strengthened defenses.'
      }
    },
    {
      id: 'growth',
      label: 'Focus on Growth',
      description: 'Welcome outsiders to expand our population',
      effects: {
        futureRefugeesDay: 8, // Refugees arrive on day 8
        futureRefugeesCount: 2,
        morale: -5,
        message: 'You sent word to nearby travelers. They will arrive in a few days.'
      }
    },
    {
      id: 'knowledge',
      label: 'Focus on Knowledge',
      description: 'Preserve wisdom and study the old ways',
      effects: {
        knowledge: 15,
        message: 'Your people dedicate themselves to learning and contemplation.'
      }
    }
  ]
};

// RANDOM EVENT POOL - Selected based on constraints
const STORY_EVENT_POOL = [
  {
    id: 'ancient_ruins',
    name: 'Ancient Ruins',
    icon: '🏛️',
    type: 'story_discovery',
    minDay: 8,
    maxDay: 20,
    description: 'Your scouts discover ancient ruins nearby. Strange symbols mark crumbling walls...',
    choices: [
      {
        id: 'excavate',
        label: 'Excavate the Ruins',
        description: 'Costs 5 wood & 5 stone to safely explore',
        cost: { wood: 5, stone: 5 },
        effects: {
          knowledge: 15,
          metalRoll: true, // Roll 1d6 for metal
          message: 'Ancient knowledge and forgotten treasures unearthed!'
        }
      },
      {
        id: 'leave',
        label: 'Leave Undisturbed',
        description: 'Respect the old places and move on',
        effects: {
          morale: 5,
          knowledge: 5,
          message: 'Some mysteries are best left alone. Your people respect the ancient site.'
        }
      }
    ]
  },
  {
    id: 'the_stranger',
    name: 'The Stranger',
    icon: '🧙',
    type: 'story_blessing',
    minDay: 10,
    maxDay: null, // Can happen any time after day 10
    description: 'A hooded figure approaches your settlement at twilight, offering a mysterious gift...',
    choices: [
      {
        id: 'harvest',
        label: 'Blessing of Harvest',
        description: 'Bountiful crops for the next 3 days',
        effects: {
          farmBonusDays: 3,
          farmBonusAmount: 5,
          message: 'The stranger smiles. Your fields will flourish for days to come.'
        }
      },
      {
        id: 'steel',
        label: 'Blessing of Steel',
        description: 'Immediate metal resources',
        effects: {
          metal: 10,
          message: 'The stranger leaves behind a cache of refined metal.'
        }
      },
      {
        id: 'unity',
        label: 'Blessing of Unity',
        description: 'New settlers join your cause',
        effects: {
          workers: 3,
          morale: 80, // Set morale to 80
          message: 'Three travelers follow the stranger and pledge themselves to your settlement.'
        }
      }
    ]
  },
  {
    id: 'refugee_caravan',
    name: 'Refugee Caravan',
    icon: '🚶',
    type: 'story_choice',
    minDay: 12,
    maxDay: 25,
    description: 'A large group of refugees fleeing danger seeks shelter. They can work, but need food...',
    choices: [
      {
        id: 'accept',
        label: 'Welcome Them',
        description: 'Gain 3 workers but lose 15 food immediately',
        cost: { food: 15 },
        effects: {
          workers: 3,
          morale: 10,
          message: 'The refugees are grateful and eager to contribute to your settlement.'
        }
      },
      {
        id: 'partial',
        label: 'Accept Some',
        description: 'Gain 1 worker, lose 5 food',
        cost: { food: 5 },
        effects: {
          workers: 1,
          morale: -5,
          message: 'You accept one family. The rest move on, disappointed.'
        }
      },
      {
        id: 'reject',
        label: 'Turn Them Away',
        description: 'Preserve resources but harm morale',
        effects: {
          morale: -15,
          message: 'Your people question your compassion as the refugees depart sadly.'
        }
      }
    ]
  },
  {
    id: 'merchant_guild',
    name: 'Merchant Guild',
    icon: '💰',
    type: 'story_choice',
    minDay: 15,
    maxDay: null,
    description: 'A wealthy merchant offers lucrative trade deals that could transform your settlement...',
    choices: [
      {
        id: 'trade_food',
        label: 'Trade Food for Resources',
        description: 'Exchange 20 food for 10 wood, 5 stone, 3 metal',
        cost: { food: 20 },
        effects: {
          wood: 10,
          stone: 5,
          metal: 3,
          message: 'The merchant departs with your surplus food, leaving valuable materials.'
        }
      },
      {
        id: 'trade_wood',
        label: 'Trade Wood for Food',
        description: 'Exchange 15 wood for 25 food',
        cost: { wood: 15 },
        effects: {
          food: 25,
          message: 'The merchant is pleased with the quality of your timber.'
        }
      },
      {
        id: 'decline',
        label: 'Politely Decline',
        description: 'Maintain self-sufficiency',
        effects: {
          morale: 3,
          message: 'Your people take pride in their independence.'
        }
      }
    ]
  },
  {
    id: 'baby_boom',
    name: 'Baby Boom',
    icon: '👶',
    type: 'story_blessing',
    minDay: 18,
    maxDay: null,
    requiresBuilding: 'nursery',
    description: 'Your nursery has brought prosperity! Multiple births occur this season...',
    choices: [
      {
        id: 'celebrate',
        label: 'Celebrate the Births',
        description: 'Add 2 immature workers, gain morale',
        effects: {
          immatureWorkers: 2, // Special handling needed
          morale: 15,
          message: 'Two children are born! The settlement rejoices at the new life.'
        }
      }
    ]
  },
  {
    id: 'divine_favor',
    name: 'Divine Favor',
    icon: '✨',
    type: 'story_blessing',
    minDay: 20,
    maxDay: null,
    description: 'A divine presence grants your settlement a boon. Choose your gift wisely...',
    choices: [
      {
        id: 'food',
        label: 'Abundance',
        description: 'Receive 20 food',
        effects: { food: 20, message: 'Granaries overflow with divine plenty.' }
      },
      {
        id: 'knowledge',
        label: 'Wisdom',
        description: 'Receive 10 knowledge',
        effects: { knowledge: 10, message: 'Ancient truths flood your scholars\' minds.' }
      },
      {
        id: 'metal',
        label: 'Iron Will',
        description: 'Receive 5 metal',
        effects: { metal: 5, message: 'Ore veins open where none existed before.' }
      },
      {
        id: 'heal',
        label: 'Healing Touch',
        description: 'All injured workers are healed',
        effects: { healAll: true, message: 'Every wound knits shut by morning.' }
      }
    ]
  },
  {
    id: 'skilled_worker',
    name: 'Skilled Artisan',
    icon: '🔨',
    type: 'story_choice',
    minDay: 10,
    maxDay: 25,
    description: 'A master craftsperson offers to join your settlement, bringing valuable skills...',
    choices: [
      {
        id: 'recruit',
        label: 'Recruit the Artisan',
        description: 'Costs 10 food, gain 1 worker + building bonus',
        cost: { food: 10 },
        effects: {
          workers: 1,
          buildingBonus: true, // Next building costs -20% (special handling)
          message: 'The artisan joins your settlement, improving your construction efficiency.'
        }
      },
      {
        id: 'decline',
        label: 'Cannot Afford',
        description: 'Let them pass by',
        effects: {
          morale: -3,
          message: 'The artisan moves on to find work elsewhere.'
        }
      }
    ]
  }
];

/**
 * Gets the story event for a specific day
 * @param {number} day - Current game day
 * @param {Object} gameState - Current game state for constraint checking
 * @returns {Object|null} Story event or null
 */
export function getStoryEvent(day, gameState) {
  // Fixed event on day 5
  if (day === 5) {
    return THE_DECISION;
  }

  // Story events only trigger every 5 days
  if (day % 5 !== 0) {
    return null;
  }

  // Filter pool by constraints
  const eligible = STORY_EVENT_POOL.filter(event => {
    // Check day constraints
    if (event.minDay && day < event.minDay) return false;
    if (event.maxDay && day > event.maxDay) return false;

    // Check building requirements
    if (event.requiresBuilding) {
      const hasBuilding = gameState.buildings.some(
        b => b.type === event.requiresBuilding && b.complete
      );
      if (!hasBuilding) return false;
    }

    return true;
  });

  // No eligible events
  if (eligible.length === 0) {
    return null; // Fall back to regular d20 event
  }

  // Random selection from eligible events
  const randomIndex = Math.floor(Math.random() * eligible.length);
  return eligible[randomIndex];
}
